import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks/use-auth';

import Button from '../common/Button';
import { buildProfileFields } from './profileFields';
import ProfileFieldsList from './ProfileFieldsList';

const CompleteProfileModalMobile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Prevent body scroll and viewport issues on iOS when modal is open
  useEffect(() => {
    const scrollY = window.scrollY;
    const originalBodyStyle = document.body.style.cssText;
    const originalHtmlStyle = document.documentElement.style.cssText;

    // Lock both html and body to prevent iOS Safari viewport issues
    document.documentElement.style.cssText = `
      overflow: hidden;
      height: 100%;
    `;
    document.body.style.cssText = `
      overflow: hidden;
      position: fixed;
      top: -${scrollY}px;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
    `;

    return () => {
      document.documentElement.style.cssText = originalHtmlStyle;
      document.body.style.cssText = originalBodyStyle;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleGoToSettings = () => {
    navigate({ to: '/dashboard/settings', search: { tab: 'profile' } });
  };

  const fields = buildProfileFields(t, user);

  return (
    <div
      className="fixed inset-0 z-50 flex max-w-full items-end overflow-hidden bg-black/40 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      style={{ touchAction: 'none' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-full overflow-x-hidden rounded-t-2xl bg-surface-card p-5 pb-8 shadow-xl"
        style={{ touchAction: 'pan-y' }}
      >
        {/* Warning Icon */}
        <div className="mb-5 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
        </div>

        {/* Title and Subtitle */}
        <div className="mb-5 text-center">
          <h2 className="font-heading text-lg font-semibold text-foreground-primary">
            {t('profileCompletion.title')}
          </h2>
          <p className="mt-1.5 font-body text-[13px] text-foreground-secondary">
            {t('profileCompletion.subtitle')}
          </p>
        </div>

        {/* Explanation */}
        <p className="mb-4 font-body text-[13px] text-foreground-secondary">
          {t('profileCompletion.explanation')}
        </p>

        {/* Fields List */}
        <ProfileFieldsList fields={fields} isDesktop={false} />

        {/* CTA Button */}
        <Button
          type="button"
          variant="solid"
          size="sm"
          fullWidth
          onClick={handleGoToSettings}
          className="rounded-[10px] py-3.5"
        >
          <span className="flex items-center justify-center gap-2">
            {t('profileCompletion.goToSettings')}
            <ArrowRight className="h-4 w-4" />
          </span>
        </Button>
      </motion.div>
    </div>
  );
};

export default CompleteProfileModalMobile;
